import React, { useEffect, useMemo, useState } from "react";
import RapidTrustRadar from "./RapidTrustRadar";
import TeamSpiderGraph from "./TeamSpiderGraph";
import useOrganization from "../../organization/hooks/useOrganization";
import { getReportList, getSurveyScores } from "../api/individualResults.api";
import getAverage from "../../../utils/getAverageScore";
import { formatReportHtml } from "../../../utils/formatReportHtml";
import { useLocation } from "react-router-dom";
import {
  getTeamCompositeScores,
  getTeamMemberScores,
  getUserSurveyScores,
} from "../../TeamcompositeResults/api/team.api";
import type { TeamMemberScore } from "../../TeamcompositeResults/api/team.api";
import { useTeam } from "../../TeamcompositeResults/contexts/TeamContext";
import { useProfile } from "../../auth/hooks/useProfile";
import { useCoach } from "../../coach/contexts/CoachProvider";

const trustKeyMap = [
  { key: "Independence", label: "Independence" },
  { key: "Advancement", label: "Advancement" },
  { key: "Exploring", label: "Exploring" },
  { key: "Caring", label: "Caring" },
  { key: "Collaboration", label: "Collaboration" },
  { key: "Safety", label: "Safety" },
];

interface Props {
  isPrint?: boolean;
  passedMembers?: TeamMemberScore[];
}

const RapidTrustInsights: React.FC<Props> = ({ isPrint = false, passedMembers }) => {
  const { currentOrganization } = useOrganization();
  const { isCoach } = useProfile();
  const { defaultSurveyId: coachSurveyId } = useCoach();

  const [trustData, setTrustData] = useState<any[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberScore[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const selectedUser = location.state?.user;
  const { selectedTeam } = useTeam();
  const clickedTeamId = location.state?.teamId;

  const questionnaireId = isCoach
    ? coachSurveyId
    : currentOrganization?.default_survey;

  /**
   * Fetch survey scores
   */
  useEffect(() => {
    if (!questionnaireId || questionnaireId <= 0) return;

    const loadScores = async () => {
      try {
        let res;

        if (selectedUser?.id) {
  res = await getUserSurveyScores({
    questionnaire_id: Number(questionnaireId),
    user_id: Number(selectedUser.id),
  });

} else if (clickedTeamId) {
  // ✅ ONLY when explicitly clicked
  if (passedMembers) {
    // Parent already fetched members — only need composite scores
    res = await getTeamCompositeScores({
      questionnaire_id: Number(questionnaireId),
      team_id: Number(clickedTeamId),
    });
  } else {
    const [compositeRes, membersRes] = await Promise.all([
      getTeamCompositeScores({
        questionnaire_id: Number(questionnaireId),
        team_id: Number(clickedTeamId),
      }),
      getTeamMemberScores({
        questionnaire_id: Number(questionnaireId),
        team_id: Number(clickedTeamId),
        limit: 15,
      }),
    ]);
    res = compositeRes;
    if (membersRes.success) {
      setTeamMembers(membersRes.data?.members ?? []);
    }
  }

} else {
  // ✅ default → individual
  res = await getSurveyScores(Number(questionnaireId));
}

        const scores = res.data?.scores || res.data || {};

        const mapped = trustKeyMap.map((item) => ({
          label: item.label,
          value: Math.round(getAverage(scores[item.key])),
        }));

        setTrustData(mapped);
      } catch (err) {
        console.error("Score loading error", err);
      }
    };

    loadScores();
  }, [questionnaireId, selectedUser, selectedTeam, clickedTeamId,passedMembers]);

  /**
   * Sort trust values
   */
  const enhancedTrustData = useMemo(() => {
    const sorted = [...trustData].sort((a, b) => b.value - a.value);

    return sorted.map((item, index) => ({
      ...item,
      type: index === 0 ? "Primary" : index === 1 ? "Secondary" : undefined,
    }));
  }, [trustData]);

  /**
   * Fetch reports
   */
  useEffect(() => {
    if (!enhancedTrustData.length) return;

    const fetchReports = async () => {
      try {
        const res = await getReportList();
        const rows = res.data.rows;

        const trustReport = rows.find(
          (r: any) => r.name === "Rapid Trust Build",
        );

        if (!trustReport) return;

        const parsedValues = JSON.parse(trustReport.values);

        const reportsToShow = isPrint
          ? enhancedTrustData
          : enhancedTrustData.slice(0, 2);

        const reportsData = reportsToShow
          .map(
            (item) =>
              parsedValues.find(
                (r: any) =>
                  r.attributes.title.toLowerCase() === item.label.toLowerCase(),
              )?.attributes,
          )
          .filter(Boolean);

        setReports(reportsData);
      } catch (err) {
        console.error("Report error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [enhancedTrustData, isPrint]);

  if (!questionnaireId) return null;

  return (
    <section className="bg-white py-5">
      <div className="max-w-[1155px] mx-auto px-5">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-[120px] items-start">
          {/* LEFT SIDE */}
          <div className="w-full lg:w-[40%] self-start lg:sticky lg:top-6 h-fit">
            <h2 className="text-2xl sm:text-3xl lg:text-[38px] text-[#26295e] mb-6 lg:mb-[30px]">
              Rapid Trust Build
            </h2>

            <div className="mb-8 lg:mb-10">
              {clickedTeamId ? (
                <TeamSpiderGraph apiMembers={passedMembers ?? teamMembers} isPrint={isPrint} />
              ) : (
                <RapidTrustRadar
                  data={trustData.map((d) => d.value)}
                  interactive={!isPrint}
                />
              )}
            </div>

            {enhancedTrustData.map((item, index) => (
              <div key={index} className="mb-6">
                <div className="flex justify-between mb-1">
                  <div>
                    {item.type && (
                      <div className="text-[12px] text-[#9aa0c3] mb-[2px]">
                        {item.type}
                      </div>
                    )}

                    <div className="font-medium">{item.label}</div>
                  </div>

                  <div>{item.value}%</div>
                </div>

                <div
                  className="h-[4px] rounded-full relative w-full"
                  style={{ backgroundColor: "#e6eaf6" }}
                >
                  <div
                    className="h-[4px] rounded-full"
                    style={{
                      width: `${item.value}%`,
                      minWidth: "6px",
                      backgroundColor: "#2f2a68",
                    }}
                  />

                  <div
                    className="absolute w-[12px] h-[12px] rounded-full -top-[5px]"
                    style={{
                      left: `calc(${item.value}% - 6px)`,
                      backgroundColor: "#2f2a68",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT SIDE */}
          <div className="w-full lg:w-[60%]">
            {loading && <p>Loading insights...</p>}

            {!loading &&
              reports.map((report, index) => (
                <div
                  key={index}
                  className={`${
                    index !== 0 ? "mt-10 print:break-before-page" : ""
                  }`}
                >
                  <span className="font-bold text-[18px] text-[#3b519c] mr-[10px]">
                    INSIGHTS
                  </span>

                  <span className="font-medium text-[18px]">
                    {report.title}
                  </span>

                  <div
                    className="text-[17px] leading-8 text-[#4b507d]"
                    dangerouslySetInnerHTML={{
                      __html: formatReportHtml(report.details),
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RapidTrustInsights;
